import { useState, useEffect, useRef } from "react";
import { useStarterKit } from "@/hooks/useStarterKit";
import { sanitizeProjectFolderName } from "@/lib/project-utils";
import type { StarterKitManifest } from "@/types";

type Selection =
  | { kind: "starter"; kitId: string; kitName: string; keepGit: boolean }
  | { kind: "blank" }
  | null;

interface Props {
  visible: boolean;
  onClose: () => void;
  onProjectCreated: (dir: string) => void | Promise<void>;
  canChangeStarterCloneMode?: boolean;
}

export function NewProjectDialog({
  visible,
  onClose,
  onProjectCreated,
  canChangeStarterCloneMode = false,
}: Props) {
  const { kits, loading, selectFolder } = useStarterKit();
  const [creating, setCreating] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [selection, setSelection] = useState<Selection>(null);
  const [slugStatus, setSlugStatus] = useState<"idle" | "checking" | "available" | "taken" | "reserved">("idle");
  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const projectNameInputRef = useRef<HTMLInputElement | null>(null);

  const isWeb = window.freshvibe.platform === "web";

  // Debounced slug availability check
  useEffect(() => {
    if (!isWeb) return; // Only check on web (Electron doesn't have port-proxy slugs)
    const slug = sanitizeProjectFolderName(projectName.trim());
    if (!slug) { setSlugStatus("idle"); return; }
    setSlugStatus("checking");
    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    slugCheckTimer.current = setTimeout(async () => {
      try {
        const resp = await fetch(`/api/preview-slugs/check/${encodeURIComponent(slug)}`, { credentials: "include" });
        if (!resp.ok) { setSlugStatus("idle"); return; }
        const data = await resp.json() as { available: boolean; reason?: string };
        setSlugStatus(data.available ? "available" : data.reason === "reserved" ? "reserved" : "taken");
      } catch {
        setSlugStatus("idle");
      }
    }, 400);
    return () => { if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current); };
  }, [projectName, isWeb]);

  // Auto-focus project name input and handle Escape to close
  useEffect(() => {
    if (!visible || creating) return;
    const focusTimer = window.setTimeout(() => {
      projectNameInputRef.current?.focus();
      projectNameInputRef.current?.select();
    }, 30);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !creating) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [visible, creating, onClose]);

  const slugBlocked = isWeb && (slugStatus === "taken" || slugStatus === "reserved");

  if (!visible) return null;

  const handleSelectKit = (kit: StarterKitManifest) => {
    setSelection((prev) => {
      // Toggle keep-git if re-selecting the same kit
      if (prev?.kind === "starter" && prev.kitId === kit.id) {
        return prev; // already selected, no change
      }
      return { kind: "starter", kitId: kit.id, kitName: kit.name, keepGit: false };
    });
  };

  const handleSelectBlank = () => {
    setSelection({ kind: "blank" });
  };

  const handleToggleKeepGit = () => {
    if (!canChangeStarterCloneMode) return;
    setSelection((prev) => {
      if (prev?.kind !== "starter") return prev;
      return { ...prev, keepGit: !prev.keepGit };
    });
  };

  const handleCreate = async () => {
    if (!selection) return;
    setCreating(true);
    try {
      const paths = await window.freshvibe.getAppPaths();
      const slug = projectName.trim()
        ? sanitizeProjectFolderName(projectName)
        : `project-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}`;
      const dir = `${paths.home}/${slug}`;
      await onProjectCreated(dir);
    } catch (err) {
      console.error("[NewProjectDialog] project creation failed:", err);
    } finally {
      setCreating(false);
    }
  };

  const handleBrowse = async () => {
    const dir = await selectFolder();
    if (dir) await onProjectCreated(dir);
  };

  const isKitSelected = (kitId: string) =>
    selection?.kind === "starter" && selection.kitId === kitId;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#021a26]/85 backdrop-blur-md animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget && !creating) onClose();
      }}
    >
      <div className="w-full max-w-md mx-4 bg-[#021a26] rounded-lg border border-line-faint shadow-[0_0_40px_rgba(0,0,0,0.3)] animate-scale-in overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-md bg-surface-hover">
              <svg
                className="w-4.5 h-4.5 text-gold"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 10.5v6m3-3H9m4.06-7.19-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z"
                />
              </svg>
            </div>
            <div>
              <h2 className="font-display text-base font-semibold text-white">
                New Project
              </h2>
              <p className="font-body text-xs text-stone-400">
                Choose a starting point for your workspace
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={creating}
            className="p-1.5 rounded-md text-stone-400 hover:text-white hover:bg-white/[0.07] transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-4 pb-6">
          {creating ? (
            <div className="py-10 text-center">
              <div className="flex items-center justify-center gap-2 text-stone-400">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce" />
                  <span
                    className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  />
                  <span
                    className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  />
                </div>
                <span className="font-body text-sm">Setting up project...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Project name input */}
              <div className="mb-4">
                <label className="block font-body text-xs font-medium text-stone-300 mb-1.5">
                  Project name
                </label>
                <div className="relative">
                  <input
                    ref={projectNameInputRef}
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="my-awesome-project"
                    className={`w-full rounded-lg border bg-surface px-3 py-2 text-sm text-stone-100 placeholder-stone-500 focus:outline-none focus:ring-2 focus:border-transparent ${
                      slugBlocked
                        ? "border-brand-red/50 focus:ring-brand-red"
                        : isWeb && slugStatus === "available"
                          ? "border-emerald-500/40 focus:ring-emerald-500"
                          : "border-white/[0.08] focus:ring-fwf-yellow/30"
                    }`}
                  />
                  {isWeb && projectName.trim() && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                      {slugStatus === "checking" && (
                        <span className="h-3.5 w-3.5 block animate-spin rounded-full border-2 border-stone-600 border-t-gold" />
                      )}
                      {slugStatus === "available" && (
                        <svg className="h-4 w-4 text-emerald-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                      )}
                      {slugBlocked && (
                        <svg className="h-4 w-4 text-brand-red" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                      )}
                    </span>
                  )}
                </div>
                {projectName.trim() && (
                  <div className="mt-1 space-y-0.5">
                    {slugBlocked && (
                      <p className="text-[0.75rem] text-brand-red font-medium">
                        {slugStatus === "reserved" ? "This name is reserved." : "This name is already taken."} Choose a different name.
                      </p>
                    )}
                    <p className="text-[11px] text-stone-500">
                      Folder: <span className="font-mono text-stone-400">{sanitizeProjectFolderName(projectName)}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Kit selection */}
              {loading ? (
                <div className="flex items-center gap-3 py-8 text-sm text-stone-400">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-stone-600 border-t-gold" />
                  Loading starter projects...
                </div>
              ) : (
                <div className="space-y-2">
                  {kits.map((kit) => (
                    <div key={kit.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectKit(kit)}
                        className={`w-full rounded-lg border px-4 py-3 text-left transition-all ${
                          isKitSelected(kit.id)
                            ? "border-fwf-yellow/45 bg-surface-hover shadow-[0_8px_24px_rgba(254,198,1,0.06)]"
                            : "border-line-faint bg-surface hover:border-fwf-yellow/25 hover:bg-surface-hover"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface-hover text-base">
                              {kit.icon || "\uD83D\uDE80"}
                            </div>
                            <div>
                              <p className={`font-display text-sm font-medium ${isKitSelected(kit.id) ? "text-gold" : "text-white"}`}>
                                {kit.name}
                              </p>
                              <p className="mt-0.5 text-xs text-stone-400">{kit.description}</p>
                            </div>
                          </div>
                          <span
                            className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                              isKitSelected(kit.id) ? "border-gold bg-gold" : "border-line bg-transparent"
                            }`}
                          >
                            {isKitSelected(kit.id) && (
                              <svg className="h-2.5 w-2.5 text-navy" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                              </svg>
                            )}
                          </span>
                        </div>
                      </button>
                      {/* Keep git toggle — appears on hover when selected */}
                      {canChangeStarterCloneMode && isKitSelected(kit.id) && (() => {
                        const keepGit = selection?.kind === "starter" && selection.keepGit;
                        return (
                          <button
                            type="button"
                            onClick={handleToggleKeepGit}
                            className="mt-1.5 ml-11 flex items-center gap-2 text-[0.75rem] text-stone-500 opacity-0 transition-all duration-200 hover:opacity-100 hover:text-stone-300 focus:opacity-100"
                            title="Toggle between template mode and full git clone"
                          >
                            <span className={`flex h-3.5 w-7 rounded-full transition-colors ${keepGit ? "bg-fwf-yellow/60" : "bg-surface-strong"}`}>
                              <span className={`mt-[1px] ml-[2px] h-2.5 w-2.5 rounded-full bg-white transition-transform ${keepGit ? "translate-x-3" : ""}`} />
                            </span>
                            <span>{keepGit ? "Keep git history (clone)" : "Use as template (no git history)"}</span>
                          </button>
                        );
                      })()}
                    </div>
                  ))}

                  {/* Blank project */}
                  <button
                    type="button"
                    onClick={handleSelectBlank}
                    className={`w-full rounded-lg border px-4 py-3 text-left transition-all ${
                      selection?.kind === "blank"
                        ? "border-fwf-yellow/45 bg-surface-hover shadow-[0_8px_24px_rgba(254,198,1,0.06)]"
                        : "border-line-faint bg-surface hover:border-fwf-yellow/25 hover:bg-surface-hover"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface-hover transition-colors ${selection?.kind === "blank" ? "text-gold" : "text-stone-400"}`}>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                          </svg>
                        </div>
                        <div>
                          <p className={`font-display text-sm font-medium ${selection?.kind === "blank" ? "text-gold" : "text-white"}`}>
                            Blank Project
                          </p>
                          <p className="mt-0.5 text-xs text-stone-400">Start fresh with an empty workspace</p>
                        </div>
                      </div>
                      <span
                        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                          selection?.kind === "blank" ? "border-gold bg-gold" : "border-line bg-transparent"
                        }`}
                      >
                        {selection?.kind === "blank" && (
                          <svg className="h-2.5 w-2.5 text-navy" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                          </svg>
                        )}
                      </span>
                    </div>
                  </button>

                  {/* Browse — only on Electron, not web */}
                  {!isWeb && (
                    <button
                      type="button"
                      onClick={handleBrowse}
                      className="w-full rounded-lg border border-dashed border-white/[0.08] bg-transparent px-4 py-3 text-left transition-all hover:border-fwf-yellow/25 hover:bg-surface-hover group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface-hover text-stone-400 group-hover:text-gold transition-colors">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-display text-sm font-medium text-white group-hover:text-gold transition-colors">
                            Open Existing Folder
                          </p>
                          <p className="text-xs text-stone-400 mt-0.5">Choose a folder already on your computer</p>
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              )}

              {/* Create button */}
              {selection && (
                <button
                  type="button"
                  disabled={slugBlocked || !projectName.trim()}
                  onClick={() => void handleCreate()}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-gold px-4 py-2.5 text-sm font-semibold text-navy hover:bg-gold-dark transition-all active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create project
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m13.5 4.5 7.5 7.5-7.5 7.5m7.5-7.5H3" />
                  </svg>
                </button>
              )}
            </>
          )}
        </div>

        {/* Footer hint */}
        {!creating && (
          <div className="border-t border-line-faint px-6 py-3">
            <p className="font-body text-[11px] text-stone-500 text-center">
              Your agent will switch to the new workspace automatically
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
